import type {
  SmartSortProduct,
  VirtualBudtenderProduct,
} from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { CardCarouselProps } from '@iheartjane/menu';
import { Flex, useMobileMediaQuery } from '@iheartjane/reefer';
import type { Breadcrumb } from '@iheartjane/types';
import { useParams } from 'react-router';

import { useFacets, useVirtualBudtenderQueries } from '@jane/dm/internal';
import { useSearchContext } from '@jane/search/hooks';
import type { AlgoliaProduct } from '@jane/search/types';
import {
  DESKTOP_PRODUCTS_PER_PAGE_GRID,
  MOBILE_PRODUCTS_PER_PAGE_GRID,
  buildSearchFilter,
} from '@jane/search/util';
import type { ProductCardWithTrackingProps } from '@jane/shared-ecomm/components';
import { useMyHigh } from '@jane/shared-ecomm/hooks';
import { useMenu } from '@jane/shared-ecomm/providers';
import { useJaneUserId } from '@jane/shared/data-access';
import type { MenuCategoryPath, MenuProduct } from '@jane/shared/types';

import { MenuEmptyResults } from '../MenuEmptyResults/MenuEmptyResults';
import { MenuFilters } from '../MenuFilters/MenuFilters';
import { MenuNavButtons } from '../MenuNavButtons/MenuNavButtons';
import { MenuProductsGrid } from '../MenuProductsGrid/MenuProductsGrid';
import { MenuSponsoredCarousel } from '../MenuSponsoredCarousel/MenuSponsoredCarousel';

interface MenuFiltersAndProductsGridProps {
  /** If the returned products need an additional filter applied */
  additionalProductFilter?: (
    products:
      | SmartSortProduct<AlgoliaProduct>[]
      | VirtualBudtenderProduct<AlgoliaProduct>[]
  ) => (
    SmartSortProduct<AlgoliaProduct> | VirtualBudtenderProduct<AlgoliaProduct>
  )[];
  /** The current page url and title that will be used on the next page */
  breadcrumbs: Breadcrumb[];
  /** We disable ads for featured/custom category product pages */
  disableAds?: boolean;
  /** Enable the mid-grid ad row placement (category pages only) */
  enableMidGridRow?: boolean;
  /** Used to determine if Buy Again page should be displayed */
  hasPurchasedProducts?: boolean;
  /** Used to remove weights from the edit view over a certain amount */
  maxWeightOption?: number | null;
  /** Used to determine which page we're on */
  menuCategory: MenuCategoryPath;
  /** Set to ideal-width by default but needs to be fixed to render in tests */
  mode?: CardCarouselProps['mode'];
  /** Remove the default x padding applied to the filter and products */
  noPadding?: boolean;
  /** Used for product tap tracking */
  pageTitle: string;
  /** Show only these price ids (for weight-based specials) */
  showOnlyWeights?: ProductCardWithTrackingProps['showOnlyWeights'];
  /** Static filters for the page, ex flower page uses flower filter by default */
  staticFilters: string;
  /** This function is passed to the product card edit view to remove unwanted weights */
  weightFilter?: (
    weight: string,
    product: MenuProduct | AlgoliaProduct
  ) => boolean;
  /** Boolean to not open the cart drawer when adding to cart */
  withoutDrawerOnAddToCart?: boolean;
}

export const MenuFiltersAndProductsGrid = ({
  additionalProductFilter,
  breadcrumbs,
  disableAds = false,
  enableMidGridRow = false,
  hasPurchasedProducts = false,
  maxWeightOption,
  menuCategory,
  mode = 'ideal-width',
  noPadding = false,
  pageTitle,
  showOnlyWeights,
  staticFilters,
  weightFilter,
  withoutDrawerOnAddToCart,
}: MenuFiltersAndProductsGridProps) => {
  const isMobile = useMobileMediaQuery({});
  const hasVirtualBudtenderEnabled = useFlag(FLAGS.dmVirtualBudtenderEnabled);
  const {
    dynamicPlacement,
    products,
    indexName,
    isLoading,
    hasNextPage,
    numColumns,
    numHits,
    fetchNextPage,
  } = useMyHigh({
    disableAds,
    enableMidGridRow,
    productsPerPage: isMobile
      ? MOBILE_PRODUCTS_PER_PAGE_GRID
      : DESKTOP_PRODUCTS_PER_PAGE_GRID,
    staticFilters,
  });

  const { store } = useMenu();

  const { searchState } = useSearchContext();

  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters,
  });

  const { facets, isLoading: isLoadingFacets } = useFacets({
    searchFilter,
    searchQuery: searchState.searchText ?? undefined,
    storeId: Number(store.id),
  });

  const {
    data: virtualBudtenderQueries,
    isLoading: isLoadingVirtualBudtenderQueries,
  } = useVirtualBudtenderQueries({
    storeId: Number(store.id),
    enabled: hasVirtualBudtenderEnabled,
  });

  const isSpecialsPage = menuCategory === 'specials';
  const isBrandSpecialsPage = menuCategory === 'jane-gold';

  const isFilteringProducts =
    Object.values(searchState).filter(Boolean).length > 0;

  const userId = useJaneUserId();
  const { featured_category, rowId: rowIdParam } = useParams<{
    featured_category: string;
    rowId: string;
  }>();

  const isBuyAgainRow =
    rowIdParam &&
    store.custom_row_settings?.some(
      (r) => r.id === Number(rowIdParam) && r.row_type === 'buy_again_row'
    );

  if (!isFilteringProducts && numHits === 0 && isSpecialsPage && !isLoading) {
    return <MenuNavButtons isMobile={isMobile} />;
  }

  if (
    (featured_category === 'buy-again' || isBuyAgainRow) &&
    (!userId || !hasPurchasedProducts)
  ) {
    return <MenuEmptyResults />;
  }

  return (
    <Flex flexDirection="column">
      <MenuFilters
        isLoadingProducts={isLoading}
        menuCategory={menuCategory}
        noPadding={noPadding}
        searchResultFacets={facets}
        searchResultFacetsLoading={isLoadingFacets}
        totalResults={numHits}
        showOnlyWeights={showOnlyWeights}
        staticFilters={staticFilters}
        isLoadingVirtualBudtenderQueries={isLoadingVirtualBudtenderQueries}
        virtualBudtenderQueries={virtualBudtenderQueries}
      />
      {!isSpecialsPage && !isBrandSpecialsPage && !disableAds && (
        <MenuSponsoredCarousel mode={mode} menuCategory={menuCategory} />
      )}
      <MenuProductsGrid
        additionalProductFilter={additionalProductFilter}
        breadcrumbs={breadcrumbs}
        pageTitle={pageTitle}
        products={products}
        dynamicPlacement={dynamicPlacement}
        indexName={indexName}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        maxWeightOption={maxWeightOption}
        noPadding={noPadding}
        numColumns={numColumns}
        numHits={numHits}
        fetchNextPage={fetchNextPage}
        showOnlyWeights={showOnlyWeights}
        weightFilter={weightFilter}
        withoutDrawerOnAddToCart={withoutDrawerOnAddToCart}
      />
    </Flex>
  );
};
